<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['close', 'created']);

const { t } = useI18n();
const store = useStore();
const dialogRef = ref(null);

const searchQuery = ref('');
const groupName = ref('');
const selectedAgentIds = ref([]);
const isCreating = ref(false);

const agents = useMapGetter('agents/getAgents');
const currentUser = useMapGetter('getCurrentUser');

const filteredAgents = computed(() => {
  const query = searchQuery.value.toLowerCase().trim();
  return agents.value
    .filter(agent => agent.id !== currentUser.value?.id)
    .filter(agent => {
      if (!query) return true;
      return (
        agent.name?.toLowerCase().includes(query) ||
        agent.email?.toLowerCase().includes(query)
      );
    });
});

const MIN_GROUP_MEMBERS = 2;

const selectableAgents = computed(() =>
  agents.value.filter(agent => agent.id !== currentUser.value?.id)
);

const hasEnoughAgents = computed(
  () => selectableAgents.value.length >= MIN_GROUP_MEMBERS
);

const canCreate = computed(
  () =>
    hasEnoughAgents.value && selectedAgentIds.value.length >= MIN_GROUP_MEMBERS
);

const membersHint = computed(() =>
  t('STAFF_CHAT.GROUP_MEMBERS_HINT', {
    selected: selectedAgentIds.value.length,
    required: MIN_GROUP_MEMBERS,
  })
);

const isAgentSelected = agentId => selectedAgentIds.value.includes(agentId);

const toggleAgent = agentId => {
  if (isAgentSelected(agentId)) {
    selectedAgentIds.value = selectedAgentIds.value.filter(
      id => id !== agentId
    );
    return;
  }
  selectedAgentIds.value = [...selectedAgentIds.value, agentId];
};

const createGroup = async () => {
  if (!canCreate.value || isCreating.value) return;

  isCreating.value = true;
  try {
    const chat = await store.dispatch('staffChats/createStaffGroupChat', {
      userIds: selectedAgentIds.value,
      name: groupName.value.trim(),
    });
    dialogRef.value?.close();
    emit('created', chat);
  } catch {
    useAlert(t('STAFF_CHAT.CREATE_GROUP_ERROR'));
  } finally {
    isCreating.value = false;
  }
};

const handleClose = () => {
  emit('close');
};

onMounted(() => {
  dialogRef.value?.open();
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="$t('STAFF_CHAT.SELECT_GROUP_MEMBERS')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="handleClose"
  >
    <div class="flex flex-col gap-3 min-w-[20rem]">
      <input
        v-model="groupName"
        type="text"
        class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-solid-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
        :placeholder="$t('STAFF_CHAT.GROUP_NAME_PLACEHOLDER')"
      />
      <p class="text-xs text-n-slate-11">
        {{ membersHint }}
      </p>
      <p v-if="!hasEnoughAgents" class="text-xs text-n-ruby-11">
        {{ $t('STAFF_CHAT.INSUFFICIENT_TEAM_MEMBERS') }}
      </p>
      <input
        v-model="searchQuery"
        type="search"
        class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-solid-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
        :placeholder="$t('STAFF_CHAT.SEARCH_PLACEHOLDER')"
      />
      <ul class="flex flex-col gap-1 max-h-64 overflow-y-auto">
        <li v-for="agent in filteredAgents" :key="agent.id">
          <div
            class="flex items-center w-full gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-n-alpha-2"
            :class="{ 'opacity-50 pointer-events-none': isCreating }"
            @click="toggleAgent(agent.id)"
          >
            <Checkbox
              :model-value="isAgentSelected(agent.id)"
              class="pointer-events-none"
            />
            <Avatar
              :name="agent.name"
              :src="agent.thumbnail"
              :size="32"
              rounded-full
            />
            <div class="flex flex-col items-start min-w-0">
              <span class="text-sm font-medium truncate text-n-slate-12">
                {{ agent.name }}
              </span>
              <span class="text-xs truncate text-n-slate-11">
                {{ agent.email }}
              </span>
            </div>
          </div>
        </li>
      </ul>
      <div class="flex justify-end">
        <NextButton
          :label="$t('STAFF_CHAT.CREATE_GROUP')"
          :disabled="!canCreate || isCreating"
          :is-loading="isCreating"
          @click="createGroup"
        />
      </div>
    </div>
    <template #footer />
  </Dialog>
</template>
